import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { array, concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import EditableField from 'editor/components/editable-field';
import Part from 'editor/components/song-editor/part';
import TrackEditor from 'editor/components/track-editor';
import add from 'editor/helpers/add';
import eq from 'editor/helpers/eq';
import not from 'editor/helpers/not';
import pick from 'editor/helpers/pick';
import { CompositeCommand } from 'editor/models/command';
import Drumset from 'editor/models/drumset';
import DrumsetFile from 'editor/models/drumset-file';
import Midi from 'editor/models/midi';
import { SectionType } from 'editor/models/song';
import Song, {
  MidiEvent,
  PartType,
  SongPart,
  SongTrack,
  normalizeMidiEvents,
} from 'editor/models/song';
import {
  AddPartCommand,
  AddTrackCommand,
  RemovePartCommand,
  RemoveTrackCommand,
  RenameSongCommand,
  RenameTrackCommand,
  ReplaceTrackCommand,
  SortPartsCommand,
  SetAutoPilotPerPartCommand,
  UnlinkTrackCommand,
  UpdateDrumsetCommand,
  UpdatePlayAtCommand,
  UpdatePlayForCommand,
  UpdateTempoCommand,
  SetAutoPilotCommand,
  SetAutoPilotLoopCommand,
  SetAutoPilotOutroPlayAfterCommand,
} from 'editor/models/song-commands';
import disabled from 'editor/modifiers/disabled';
import onKey from 'editor/modifiers/on-key';
import selected from 'editor/modifiers/selected';
import sortable from 'editor/modifiers/sortable';
import type PasteboardService from 'editor/services/pasteboard';
import ProjectManagerService from 'editor/services/project-manager';
import SongPlayerService, { type SelectedTrack } from 'editor/services/song-player';
import type VirtualPedalService from 'editor/services/virtual-pedal';
import UndoManagerService from 'editor/services/undo-manager';
import { ChokeGroup } from 'editor/services/virtual-pedal';
import nameFilter from 'editor/utils/name-filter';
import playInstrument from 'editor/utils/play-instrument';
import wait from 'editor/utils/wait';
import { modifier } from 'ember-modifier';
import Button from './button';
import onClickOutside from 'editor/modifiers/on-click-outside';

import velcro from 'ember-velcro/modifiers/velcro';
import { size } from '@floating-ui/dom';
const manualResize = modifier(
  (
    $element: HTMLElement,
    [song, drumset, selectedTrack, isTrackMaximised]: [
      Song | undefined,
      Drumset | undefined,
      SelectedTrack | undefined,
      boolean,
    ],
    { resizable, handle }: { resizable: string[]; handle: string },
  ) => {
    let dragOffsetY = 0,
      availableHeight = 0,
      beforeHandle: HTMLElement[] = [],
      afterHandle: HTMLElement[] = [];

    const cancelDragNDrop = (e: DragEvent) => {
      e.preventDefault();
    };

    const onMouseMove = (ev: MouseEvent) => {
      ev.preventDefault();
      let y = ev.clientY - dragOffsetY;
      beforeHandle.forEach((el) => {
        el.style.height = `${parseInt(el.style.height) + y}px`;
      });
      afterHandle.forEach((el) => {
        el.style.height = `${parseInt(el.style.height) - y}px`;
      });
      dragOffsetY = ev.clientY;
    };

    const onPointerUp = (ev: PointerEvent) => {
      document.removeEventListener('pointerup', onPointerUp);
      $handle.releasePointerCapture(ev.pointerId);
    };

    const onMouseUp = () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);

      dragOffsetY = 0;
    };

    const onPointerDown = (ev: PointerEvent) => {
      document.addEventListener('pointerup', onPointerUp);
      $handle.setPointerCapture(ev.pointerId);
    };

    const onMouseDown = (ev: MouseEvent) => {
      dragOffsetY = ev.clientY;

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const children: HTMLElement[] = <HTMLElement[]>(
      Array.from($element.childNodes).filter((n) => n.nodeType === 1)
    );
    const $handle = children.find((n) => n.matches(handle)) as HTMLElement;
    const resizableElements = children.filter((n) => resizable.some((r) => n.matches(r)));
    const fixedSizeELements = children.filter((n) => !resizableElements.includes(n));
    beforeHandle = resizableElements.filter((el) => el.offsetTop < $handle.offsetTop);
    afterHandle = resizableElements.filter((el) => el.offsetTop > $handle.offsetTop);

    const setup = () => {
      if (isTrackMaximised) {
        resizableElements.forEach((el) => {
          el.style.height = '';
        });
        return;
      }
      const fixedHeight = fixedSizeELements.reduce((acc, el) => acc + el.clientHeight, 0);
      let flexibleHeight = resizableElements.reduce((acc, el) => {
        return acc + el.clientHeight;
      }, 0);
      availableHeight = $element.clientHeight - fixedHeight;
      resizableElements.forEach((el) => {
        if (el.clientHeight <= availableHeight / 2) {
          if (el != document.querySelector('.track')) {
            el.style.height = `${el.clientHeight}px`;
          }
          availableHeight -= el.clientHeight;
          flexibleHeight -= el.clientHeight;
        }
      });
      resizableElements.forEach((el) => {
        if (el.style.height === '') {
          el.style.height = `${(el.clientHeight / flexibleHeight) * availableHeight}px`;
        }
      });
      //Are there now scrollbars?
      if ($element.clientHeight < $element.scrollHeight) {
        const scrollbarHeight = $element.scrollHeight - $element.clientHeight;
        resizableElements.forEach((el) => {
          el.style.height = `${
            el.clientHeight - scrollbarHeight / resizableElements.length
          }px`;
        });
      }

      // A timeout is necessary to make sure the resizing has settled before scrolling the selected part into view
      // however, in #431, it was requested that we remove this scroll
      // setTimeout(() => {
      //   $element.querySelector('.parts .selected')?.scrollIntoView({
      //     block: 'center',
      //   });
      // }, 10);
    };

    setup();

    // I'm dispatching this event in order to make sure that the modifier is "using"
    //  the song argument so it will be re-run when the song changes
    $element.dispatchEvent(
      new CustomEvent('manualResize', {
        detail: { song, drumset, selectedTrack },
      }),
    );

    $handle.addEventListener('mousedown', onMouseDown);
    $handle.addEventListener('pointerdown', onPointerDown);
    $handle.addEventListener('dragstart', cancelDragNDrop);
    window.addEventListener('resize', setup);

    return () => {
      window.removeEventListener('resize', setup);
      $handle.removeEventListener('mousedown', onMouseDown);
      $handle.removeEventListener('pointerdown', onPointerDown);
      $handle.removeEventListener('dragstart', cancelDragNDrop);
    };
  },
);

interface SongEditorSignature {
  Element: HTMLDivElement;
  Args: {
    song: Song;
    saveSong: () => void;
  };
  Blocks: {
    // default: [];
  };
}

export default class SongEditorComponent extends Component<SongEditorSignature> {
  @service declare projectManager: ProjectManagerService;
  @service declare songPlayer: SongPlayerService;
  @service declare virtualPedal: VirtualPedalService;
  @service declare undoManager: UndoManagerService;
  @service declare pasteboard: PasteboardService;

  tapTempoBuffer: number[] = Array(4).fill(0);

  @tracked isTrackMaximized = false;
  @tracked isAutopilotMenuOpen = false;
  @tracked isDrumsetMenuOpen = false;
  @tracked openFolderId: string | null = null;
  @tracked dontclose = false
  @tracked dontclosefolder = false
  constructor(owner: unknown, args: SongEditorSignature['Args']) {
    super(owner, args);
  }

  get song() {
    return this.args.song;
  }

  @action
  renameSong(name: string) {
    this.undoManager.executeCommand(new RenameSongCommand(this.song, name));
  }

  get drumsets() {
    return this.projectManager.drumsets.toSorted((d1, d2) =>
      d1.name.localeCompare(d2.name),
    );
  }
  get drumFolders() {
    return this.projectManager.drumFolders;
  }

  get looseDrumsets() {
      return this.projectManager.drumsOnTheLoose
  }

  get isProjectDRMX() {
    return this.projectManager.isProjectDRMX;
  }

  get drumsetFile() {
    // was this - see comment on https://github.com/SingularSound/BBMO/pull/530
    // return this.projectManager.findDefaultDrumsetByName(this.song.defaultDrumsetName);
    return this.projectManager.findDrumset(this.song.drumset);
  }
  
  get drumset() {
    return this.drumsetFile.drumset!;
  }

  @action
  async setDrumset(drumset: DrumsetFile) {
    await drumset.parse()
    this.undoManager.executeCommand(
      new UpdateDrumsetCommand(this.song, this.drumsetFile, drumset)
    );
  }

  get tempoDisabled() {
    return this.autopilot && (this.songPlayer.isPlaying || this.virtualPedal.isPlaying);
  }

  anchorIdFor(folder: { cleanId?: string; name: string }) {
    if (!folder) {
      return '';
    }
    return `folder-${folder.cleanId ?? folder.name.replace(/\s+/g, '-').toLowerCase()}`;
  }

  get openedFolderAnchorId(): string | null {
    // here, the user clicked on a folder
    if (this.openFolderId && this.openFolderId !== '') {
      return this.openFolderId;
    }

    // Otherwise, automatically open the folder that contains the current drumset
    if (!this.looseDrumsets?.includes?.(this.drumsetFile)) {
      const folder = this.drumFolders?.find?.(f => f.songs?.includes?.(this.drumsetFile));
      return folder ? this.anchorIdFor(folder) : null;
    }

    // if the current drumset is loose, and the user hasn't opened any folder, then no folder is opened
    return null;
  }

  @action 
  toggleFolder(folder: any, e: MouseEvent) {
    this.dontclose = true;
    const id = this.anchorIdFor(folder);
    this.openFolderId = this.openFolderId === id ? null : id;
  }

  @action 
  closeFolder() {
    if (this.dontclosefolder) {
      this.dontclosefolder = false;
      return;
    }
    this.openFolderId = null;
  }

  setTempo: (() => void) | undefined;
  @action
  updateTempo(bpm: number, debounceTimeout: number = 500) {
    if (!this.setTempo) {
      // create a debouncable function
      this.setTempo = (() => {
        // capture the original bpm
        const originalBpm = this.song.bpm;
        return () => {
          this.undoManager.executeCommand(
            new UpdateTempoCommand(this.song, originalBpm, this.song.bpm),
          );
          this.setTempo = undefined;
          this.tapTempoBuffer = Array(4).fill(0);
        };
      })();
    }
    // update the bpm live
    this.song.bpm = bpm;
    // Debounce the undo command to avoid spamming the undo stack
    // eslint-disable-next-line ember/no-runloop
    debounce(this, this.setTempo, debounceTimeout);
  }

  @action
  tapTempo(ev: MouseEvent) {
    if (this.tempoDisabled) {
      return;
    }
    ev.preventDefault();
    const now = performance.now();
    const last = this.tapTempoBuffer.shift()!;
    this.tapTempoBuffer.push(now);
    if (last > 0) {
      const elapsed = now - last;
      const bpm = Math.round((this.tapTempoBuffer.length / elapsed) * 60000);
      this.updateTempo(bpm, 1600);
    }
  }

  get tracks() {
    return this.args.song.tracks;
  }

  get selectedTrack(): SelectedTrack | undefined {
    return this.songPlayer.selectedTrack;
  }

  get loop() {
    return this.songPlayer.loop;
  }

  @action
  toggleLoop() {
    this.songPlayer.loop = !this.loop;
  }

  get autopilot() {
    return this.args.song.autoPilotEnabled && this.isProjectDRMX;
  }

  @action
  toggleAutoPilot() {
    const autopilotVal = !this.autopilot;
    if (autopilotVal && this.isAutopilotAtDefaults) {
      const commands = [
        new SetAutoPilotCommand(this.args.song, true),
        ...this.buildAutopilotDefaultCommands(),
      ];
      const command = new CompositeCommand('Enable autopilot with defaults', commands);
      this.undoManager.executeCommand(command);
      return;
    }
    this.undoManager.executeCommand(
      new SetAutoPilotCommand(this.args.song, autopilotVal),
    );
  }

  get autopilotLoop() {
    return this.args.song.autoPilotLoop;
  }

  @action
  toggleAutoPilotLoop() {
    const autopilotLoopVal = !this.autopilotLoop;
    this.undoManager.executeCommand(
      new SetAutoPilotLoopCommand(this.args.song, autopilotLoopVal),
    );
  }

  get trackPosition() {
    return this.songPlayer.trackPosition;
  }

  @action
  setTrackPosition(position: number) {
    this.songPlayer.trackPosition = position;
  }

  @action
  play(fromTheBeginning: boolean = false) {
    if (fromTheBeginning) {
      this.songPlayer.playFrom(0);
    } else {
      this.songPlayer.playFrom(this.trackPosition - (this.selectedTrack?.trigPos ?? 0));
    }
  }

  @action
  stop() {
    this.songPlayer.stop(0, 0, true);
  }

  @action
  togglePlay(fromTheBeginning: boolean = false) {
    if (this.songPlayer.isPlaying) {
      const playbackStartPos = this.songPlayer.trackPosition;
      this.stop();
      this.songPlayer.trackPosition = playbackStartPos;
    } else {
      this.play(fromTheBeginning);
    }
  }

  get canAddPart() {
    return this.song.parts.length < 32;
  }

  @action
  addPart(part?: SongPart) {
    if (this.canAddPart) {
      this.undoManager.executeCommand(new AddPartCommand(this.args.song, part));
    }
  }

  @action
  removePart(part: SongPart) {
    this.undoManager.executeCommand(new RemovePartCommand(this.args.song, part));
    if (part.tracks.includes(this.songPlayer.track!)) {
      this.songPlayer.stop();
      this.songPlayer.findTrack(this.args.song);
    }
  }

  @action
  pastePart(part: SongPart) {
    if (this.canAddPart) {
      const copiedPart = this.pasteboard.retrieve('part');
      if (copiedPart) {
        this.undoManager.executeCommand(
          new AddPartCommand(this.args.song, part, copiedPart),
        );
      }
    }
  }

  @action
  duplicatePart(part: SongPart) {
    if (this.canAddPart) {
      this.undoManager.executeCommand(new AddPartCommand(this.args.song, part, part));
    }
  }

  @action
  selectTrack(part: SongPart, sectionType: SectionType, index: number) {
    this.songPlayer.selectTrack(part, sectionType, index);
  }

  @action
  async addTrack(part: SongPart, sectionType: SectionType) {
    const opts = {
      timeSignatureNumerator: part.timeSignatureNumerator,
      timeSignatureDenominator: part.timeSignatureDenominator,
    };
    await this.undoManager.executeCommand(new AddTrackCommand(part, sectionType, this.song.bpm, opts));
    this.songPlayer.stop();
    this.songPlayer.selectTrack(part, sectionType, part.fills.length - 1);
  }

  @action
  copyTrack() {
    if (this.songPlayer.selectedTrack) {
      this.pasteboard.store('track', [this.song, this.songPlayer.selectedTrack]);
    }
  }

  get hasCopiedTrack() {
    return this.pasteboard.hasCopy('track');
  }

  @action
  pasteTrack() {
    if (this.songPlayer.selectedTrack) {
      const data = <[Song, SelectedTrack] | undefined>this.pasteboard.retrieve('track');
      if (data && this.selectedTrack) {
        const [song, copiedTrack] = data;
        const { part, sectionType, sectionIndex } = this.songPlayer.selectedTrack;
        // If we’re pasting into the same song, then we use the same track and they remain linked.
        // If we’re pasting into a different song, then we copy the track so that edits aren’t shared across two songs (BUG)
        const pastedTrack =
          song === this.song ? copiedTrack.track : copiedTrack.track.copy();
        this.undoManager.executeCommand(
          new CompositeCommand('Paste track', [
            new ReplaceTrackCommand(part, sectionType, sectionIndex, pastedTrack),
          ]),
        );
      }
    }
  }

  get isLinkedTrack() {
    if (!this.selectedTrack) {
      return false;
    }
    const { track } = this.selectedTrack;
    return (
      this.song.allParts.flatMap((part) => part.tracks).filter((t) => t === track)
        .length > 1
    );
  }

  get onMac() {
    return this.projectManager.onMac;
  }

  @action
  unlinkTrack() {
    if (this.songPlayer.selectedTrack) {
      const { part, sectionType, sectionIndex } = this.songPlayer.selectedTrack;
      this.undoManager.executeCommand(
        new UnlinkTrackCommand(part, sectionType, sectionIndex),
      );
    }
  }

  @action
  async importMidiFile(
    part?: SongPart,
    sectionType?: SectionType,
    sectionIndex?: number,
    files?: FileList,
  ) {
    if (!files && !this.selectedTrack) {
      return;
    }
    let selectPart = false;
    if (!(part instanceof SongPart)) {
      part = this.selectedTrack!.part;
      sectionType = this.selectedTrack!.sectionType;
      sectionIndex = this.selectedTrack!.sectionIndex;
      selectPart = true;
    }
    // eslint-disable-next-line no-undef
    let handle: FileSystemFileHandle;
    if (!files) {
      try {
        [handle] = await window.showOpenFilePicker({
          id: 'midi',
          startIn: 'documents',
          multiple: false,
          types: [
            { description: 'MIDI file', accept: { 'audio/midi': ['.mid', '.midi'] } },
          ],
        });
      } catch (err: any) {
        if (err.name === 'AbortError' && /user aborted/i.test(err.message)) {
          return;
        }
        throw err;
      }
      try {
        await wait(async () => {
          const file = await handle.getFile();
          this.importMidiHelper(file, part, sectionType, sectionIndex, selectPart);
        });
      } catch (err) {
        if (err instanceof DOMException) {
          console.error('DOMException', { name: err.name, message: err.message });
          alert('Unable to import MIDI track\n' + err.message);
        } else {
          console.error(err);
          alert('Unable to import MIDI track');
        }
      }
    } else {
      this.importMidiFiles(part, sectionType, sectionIndex, files);
    }
  }

  @action
  async importMidiFiles(
    part?: SongPart,
    sectionType?: SectionType,
    sectionIndex?: number,
    files?: FileList,
  ) {
    let selectPart = false;
    if (!(part instanceof SongPart)) {
      part = this.selectedTrack!.part;
      sectionType = this.selectedTrack!.sectionType;
      sectionIndex = this.selectedTrack!.sectionIndex;
      selectPart = true;
    }

    // eslint-disable-next-line no-undef
    let handles: FileSystemFileHandle[];
    if (!files || files.length === 0) {
      // If we are opening a dialog to select file(s), we don't have the file(s) yet
      try {
        handles = await window.showOpenFilePicker({
          id: 'midi',
          startIn: 'documents',
          multiple: true,
          types: [
            { description: 'MIDI file', accept: { 'audio/midi': ['.mid', '.midi'] } },
          ],
        });
      } catch (err: any) {
        if (err.name === 'AbortError' && /user aborted/i.test(err.message)) {
          return;
        }
        throw err;
      }
      if (handles.length + sectionIndex > 8) {
        alert('Only 8 fills are supported');
        return;
      }
      await Promise.all(
        handles.map(async (handle) => {
          const file = await handle.getFile();
          this.importMidiHelper(file, part, sectionType, sectionIndex, selectPart);
          sectionIndex++;
        }),
      );
    } else {
      if (files.length + sectionIndex > 8) {
        alert('Only 8 fills are supported');
        return;
      }
      // If we are dragging and dropping file(s), we already have the file(s)
      try {
        await wait(async () => {
          for (var i = 0; i < files.length; i++) {
            const file = files?.[i];
            this.importMidiHelper(file, part, sectionType, sectionIndex, selectPart);
            sectionIndex++;
          }
        });
      } catch (err) {
        if (err instanceof DOMException) {
          console.error('DOMException', { name: err.name, message: err.message });
          alert('Unable to import MIDI track\n' + err.message);
        } else {
          console.error(err);
          alert('Unable to import MIDI track');
        }
      }
    }
  }

  @action
  async showMenu(show: boolean) {
    this.isAutopilotMenuOpen = show;
  }

  @action
  async importMidiHelper(
    file: File,
    part?: SongPart,
    sectionType?: SectionType,
    sectionIndex?: number,
    selectPart?: boolean,
  ) {
    if (file.size === 0) {
      console.error(`Unable to read MIDI file "${file.name}": file is empty (0 bytes)`);
      alert(`Unable to read MIDI file "${file.name}". No data`);
      return;
    }
    const buffer = await file.arrayBuffer();
    let midi: Midi;
    try {
      midi = Midi.parse(buffer);
    } catch (err) {
      console.error(`Unable to parse MIDI file "${file.name}" (${file.size} bytes)`, err);
      alert(`Unable to read MIDI file "${file.name}"`);
      return;
    }
    if (midi.header.type === 2) {
      console.error(`MIDI file "${file.name}" is type 2, which is not supported`);
      alert(`MIDI type 2 not supported: "${file.name}"`);
      return;
    }

    const midiTrack = midi.tracks.find((track) => track.tickCount > 0);
    if (midiTrack) {
      const midiEvents = midi.tracks
        .filter((t) => t.tickCount > 0)
        .flatMap((t) => t.events)
        .filter((e: any) => e.type === 'noteOn' || e.type === 'noteOff')
        .map((e: any) => {
          const { type, note, velocity, tick } = e;
          return new MidiEvent(tick, type === 'noteOn' ? 0 : 1, note, velocity);
        });

      const newTrack = new SongTrack('Imported Track');
      newTrack.name = file.name.split('.').slice(0, -1).join('.');
      newTrack.info.bpm = midiTrack.bpm;
      newTrack.info.timeSignatureNumerator = midiTrack.timeSignatureNumerator;
      newTrack.info.timeSignatureDenominator = midiTrack.timeSignatureDenominator;
      newTrack.info.barLength = newTrack.info.tickToQuarter * newTrack.info.timeSignatureNumerator * (4 / newTrack.info.timeSignatureDenominator);
      newTrack.info.tickCount = newTrack.info.barLength * midiTrack.barCouunt;
      newTrack.info.events = normalizeMidiEvents(midiEvents, newTrack.info);
      const command = new ReplaceTrackCommand(
        part!,
        sectionType!,
        sectionIndex!,
        newTrack,
      );
      if (selectPart) {
        command.on('execute', () => {
          this.songPlayer.selectTrack(part!, sectionType!, sectionIndex!);
        });
      }
      this.undoManager.executeCommand(command);
    }
  }

  @action
  async exportMidiFile() {
    if (!this.selectedTrack) {
      return;
    }
    const { track } = this.selectedTrack;
    let newName;
    // eslint-disable-next-line no-undef
    let handle: FileSystemFileHandle;
    try {
      handle = await window.showSaveFilePicker({
        id: 'midi',
        startIn: 'documents',
        suggestedName: `${track.name}.mid`,
        types: [
          { description: 'MIDI file', accept: { 'audio/midi': ['.mid'] } },
          { description: 'WAV file', accept: { 'audio/wav': ['.wav'] } },
        ],
      });
      newName = handle.name.replace(/\.mid$/i, '');
    } catch (err: any) {
      if (err.name === 'AbortError' && /user aborted/i.test(err.message)) {
        return;
      }
      throw err;
    }

    try {
      await wait(async () => {
        const fileExtension = handle.name.split('.').pop();

        let fileData: ArrayBuffer;
        switch (fileExtension) {
          case 'mid':
            fileData = track.toMidi(newName);
            break;
          case 'wav':
            {
              // NOTE: This, virtual pedal, and song player all use similar variations of the same code
              // While not duplicated, there is definitely opportunity for refactoring
              const song = this.songPlayer.song;
              const drumset = this.songPlayer.drumset!;
              const ticksPerSecond = (track.tickToQuarter / 60) * song.bpm;
              const totalTime = track.info.tickCount / ticksPerSecond;
              const chokeGroups = new Array(15).fill(0).map(() => new ChokeGroup());

              const audioContext = new OfflineAudioContext({
                numberOfChannels: 2,
                length: Math.ceil(totalTime * 44100),
                sampleRate: 44100,
              });
              const drumsetVolume = new GainNode(audioContext, { gain: 1 });
              drumsetVolume.connect(audioContext.destination);
              const drumsetGain = drumset.volume / 100;
              drumsetVolume.gain.setValueAtTime(drumsetGain, 0);

              track.events.map((event) => {
                const onWhen = event.tick / ticksPerSecond;
                const offWhen = onWhen + event.length / ticksPerSecond;
                const instrument = drumset.instruments[event.note];
                if (instrument) {
                  if (instrument.chokeGroup > 0) {
                    const chokeGroup = chokeGroups[instrument.chokeGroup];
                    chokeGroup?.choke(onWhen, instrument.name);
                  }
                  const note = playInstrument(
                    audioContext,
                    drumsetVolume,
                    instrument,
                    onWhen,
                    offWhen,
                    event.velocity,
                  );
                  if (note) {
                    if (instrument.chokeGroup > 0) {
                      chokeGroups[instrument.chokeGroup]?.add(note, instrument.name);
                    }
                  }
                }
              });

              const audioBuffer = await audioContext.startRendering();
              fileData = Drumset.saveWaveFile(audioBuffer);
            }
            break;
          default:
            alert('Unsupported file type');
            return;
        }

        const file = await handle.createWritable();
        await file.write(fileData);
        await file.close();
      });
    } catch (err) {
      if (err instanceof DOMException) {
        console.error('DOMException', { name: err.name, message: err.message });
        alert('Unable to save MIDI track\n' + err.message);
      } else {
        console.error(err);
        alert('Unable to save MIDI track');
      }
    }
  }

  @action
  async removeTrack() {
    if (this.songPlayer.selectedTrack) {
      const { part, sectionType, sectionIndex } = this.songPlayer.selectedTrack;
      await this.undoManager.executeCommand(
        new RemoveTrackCommand(part, sectionType, sectionIndex),
      );
      this.songPlayer.stop();
      this.songPlayer.findTrack(this.songPlayer.song);
    }
  }

  @action
  renameTrack(name: string) {
    if (this.songPlayer.selectedTrack) {
      const { part, sectionType, sectionIndex } = this.songPlayer.selectedTrack;
      this.undoManager.executeCommand(
        new RenameTrackCommand(part, sectionType, sectionIndex, name),
      );
    }
  }

  @action
  sortParts(oldIndex: number, newIndex: number) {
    this.undoManager.executeCommand(new SortPartsCommand(this.song, oldIndex, newIndex));
  }

  @action
  toggleTrackMaximized() {
    this.isTrackMaximized = !this.isTrackMaximized;
  }

  @action
  closeDrumsetMenu() {
    if (this.dontclose) {
      this.dontclose = false;
      this.dontclosefolder = true
      return;
    }
    this.isDrumsetMenuOpen = false;
    this.dontclosefolder = false;
    this.openFolderId = null;
  }

  @action
  openDrumsetMenu() {
    this.isDrumsetMenuOpen = true;
  }

  @action
  handleDisabledToggleClick(isProjectDRMX: boolean) {
    if (!isProjectDRMX) {
      alert('Autopilot is only possible on the BeatBuddy 2, if you have a BeatBuddy 2 please convert your project, conversion is found in the project settings (gear icon)');
    }
  }

  get isAutopilotAtDefaults() {
    return this.song.parts.every((part) => {
      const fillsDefault = part.info.drumFillPlayAt
        .slice(0, part.fills.length)
        .every((val) => val === 0);
      return !part.info.autoPilotEnabled && part.info.playFor === 1 && fillsDefault;
    });
  }

  buildAutopilotDefaultCommands() {
    return this.song.parts.flatMap((part) => {
      const partCommands = [];
      if (!part.info.autoPilotEnabled) {
        partCommands.push(new SetAutoPilotPerPartCommand(part, true));
      }
      const mainBars = part.main?.barCount ?? 0;
      if (part.main) {
        const playFor =
          mainBars >= 8
            ? mainBars
            : mainBars === 0 || mainBars === 1
              ? 8
              : mainBars % 2 === 0
                ? 8
                : mainBars;
        partCommands.push(new UpdatePlayForCommand(part, SectionType.MAIN, playFor));
      }
      part.fills.forEach((_, index) => {
        partCommands.push(new UpdatePlayAtCommand(part, index, 0));
      });
      return partCommands;
    });
  }

  @action
  dropdownMiddleware() {
    return [
      size({
        apply({ availableHeight, elements }) {
          // Limit dropdown height to the space that’s actually available
          Object.assign(elements.floating.style, {
            maxHeight: `max-h-[40vh]`,
            overflowY: 'auto',
          });
        },
      }),
    ];
  }

  @action
  popupMiddleware() {
    return [
      {
        name: 'Max height',
        options: {},
        async fn(state: any) {
          const availableHeight = document.documentElement.clientHeight;
          const top = state.rects.reference.y + state.rects.reference.height;
          const height = availableHeight - top - 20;
          state.elements.floating.style.maxHeight = `${height}px`;
          return state;
        },
      },
    ];
  }

  <template>
    <div
      class="h-full"
      {{onKey
        "Space"
        (fn this.togglePlay false)
        cmdKey=false
        shiftKey=false
        preventDefault=true
      }}
      {{onKey "Space" (fn this.togglePlay true) shiftKey=true preventDefault=true}}
      {{manualResize
        this.song
        this.drumset
        this.selectedTrack
        this.isTrackMaximized
        resizable=(array ".parts" ".track")
        handle=".handle"
      }}
    >
      <nav aria-label="song-menu"><div
          class="grid items-center grid-cols-[1fr_minmax(auto,_3fr)_1fr] gap-2 px-2"
        >
          <div class="flex gap-2">
          </div>
          <EditableField
            @model={{this.song}}
            @field="name"
            @onChange={{this.renameSong}}
            {{!-- @filter={{nameFilter}} --}}
            class="w-full px-2 py-1 font-semibold text-center"
          >{{this.song.name}}</EditableField>
        </div>
        <div class="flex items-center justify-start w-full gap-4 p-2">
    
            <div >Drum Set:
            <div class="relative inline-block">
              <button
                  id="custom-drumset-button"
                  type="button"
                  class="w-64 px-2 py-1 pr-8 border rounded text-left
                        dark:bg-primary-darkComponent dark:border-primary-darkBorderColor
                        focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
                        overflow-hidden text-ellipsis whitespace-nowrap"
                  aria-haspopup="menu"
                  aria-expanded={{this.isDrumsetMenuOpen}}
                  {{on "click" this.openDrumsetMenu}}
                  {{onClickOutside (fn this.closeDrumsetMenu)}}
                >
                  {{this.drumsetFile.name}}
                </button>

                {{!-- right-side chevron --}}
                <span
                  class="pointer-events-none absolute inset-y-0 right-2 flex items-center"
                  aria-hidden="true"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"/>
                  </svg>
                </span>
              </div>
              {{#if this.isDrumsetMenuOpen}}
              <div
                class="z-[99] max-h-[40vh] overflow-y-auto overscroll-contain" 
                  {{velcro 
                  "#custom-drumset-button" middleware=(this.popupMiddleware)
                  placement="bottom-start"
                  strategy="fixed"}}
                >
                {{#each this.looseDrumsets as |drumset|}}
                  <nav 
                    class="min-w-64 px-2 py-1 pr-8 border text-left bg-white
                        dark:bg-primary-darkComponent dark:border-primary-darkBorderColor
                        hover:bg-[Highlight] hover:text-[HighlightText]
                  focus:bg-[Highlight] focus:text-[HighlightText] focus:outline-none
                  active:bg-[Highlight] active:text-[HighlightText]
                  aria-checked:bg-[Highlight] aria-checked:text-[HighlightText]"
                    {{on "click" (fn this.setDrumset drumset)}}
                  >
                    {{#if (eq drumset.name this.song.defaultDrumsetName)}}
                      {{#if (eq this.drumsetFile.path drumset.path)}}
                          ✓{{drumset.name}}*
                      {{else}}
                        {{drumset.name}}*
                      {{/if}}
                    {{else if (eq this.drumsetFile.path drumset.path)}}      
                      ✓{{drumset.name}}
                    {{else}}
                      {{drumset.name}}
                    {{/if}}
                  
                    {{#if (eq drumset.drmInPath false)}}
                      - HD
                    {{/if}}
                  </nav>
                {{/each}}
                {{#each this.drumFolders as |folder|}}
                  <nav
                    id={{this.anchorIdFor folder}}
                    class="relative min-w-64 px-2 py-1 pr-8 border text-left cursor-pointer bg-white
                          dark:bg-primary-darkComponent dark:border-primary-darkBorderColor
                          hover:bg-[Highlight] hover:text-[HighlightText]"
                    {{on "click" (fn this.toggleFolder folder)}}
                  >
                    <FaIcon @icon="folder" @fixedWidth={{true}} />
                    {{folder.name}}

                    <svg
                      class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4"
                      viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
                    >
                      <path d="M7.293 4.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 1 1-1.414-1.414L10.586 10 7.293 6.707a1 1 0 0 1 0-1.414z"/>
                    </svg>
                  </nav>

                  {{!-- Right-hand submenu, anchored to the folder row --}}
                  {{#if (eq this.openedFolderAnchorId (this.anchorIdFor folder))}}
                    <div
                      class="fixed z-[9999] max-h-[40vh] w-64 overflow-y-auto overscroll-contain
                            bg-white dark:bg-primary-darkComponent border rounded shadow-lg"
                      {{velcro
                        (concat "#" (this.anchorIdFor folder))
                        placement="right-start"
                        strategy="fixed"
                        middleware=(this.dropdownMiddleware)
                      }}
                      {{onClickOutside this.closeFolder}}
                    >
                      {{#each folder.songs as |drumset|}}
                        <div
                          aria-checked={{eq this.drumsetFile.path drumset.path}}
                          class="px-3 py-2 cursor-pointer truncate
                                hover:bg-[Highlight] hover:text-[HighlightText]
                                aria-checked:bg-[Highlight] aria-checked:text-[HighlightText]"
                          {{on "click" (fn this.setDrumset drumset)}}
                        >
                          {{#if (eq drumset.name this.song.defaultDrumsetName)}}
                            {{#if (eq this.drumsetFile.path drumset.path)}}✓{{/if}}{{drumset.name}}*
                          {{else if (eq this.drumsetFile.path drumset.path)}}
                            ✓{{drumset.name}}
                          {{else}}
                            {{drumset.name}}
                          {{/if}}
                          {{#if (eq drumset.drmInPath false)}} - HD{{/if}}
                        </div>
                      {{/each}}
                    </div>
                  {{/if}}
                {{/each}}
               </div> 
                {{/if}}

            </div>
          
          <label class="flex items-center gap-2 grow">
            {{! template-lint-disable no-invalid-interactive }}
            <div class="whitespace-nowrap" {{on "mousedown" this.tapTempo}}>Tempo:
              <span class="font-semibold">{{this.song.bpm}}bpm</span></div>
            <div class="flex items-center gap-4 grow w-full">
              <input
                type="range"
                disabled={{this.tempoDisabled }}
                min="40"
                max="300"
                value={{this.song.bpm}}
                class="w-full accent-primary {{if this.tempoDisabled
                        'cursor-not-allowed opacity-50 bg-gray-200'
                        ''
                      }}"
                {{on "input" (pick "target.value" this.updateTempo)}}
                 title={{if this.tempoDisabled
                    "Can't adjust tempo while song is playing"
                  }}
              />
                
              <div class="{{if (not this.isProjectDRMX) 'cursor-not-allowed opacity-50 flex items-center gap-2 flex-shrink-0' 'flex items-center gap-2 flex-shrink-0'}}"  title={{if (not this.isProjectDRMX) "Autopilot is only available in a BB2 project" ""}}>
                <span class="whitespace-nowrap">Autopilot Settings:</span>
                <div class="toggle-switch">
                  {{!-- template-lint-disable no-nested-interactive --}}
                  <input type="checkbox" id="toggle" disabled={{not this.isProjectDRMX}} checked={{this.autopilot}}
                  {{on "change" this.toggleAutoPilot}}/>
                  <label
                    title={{if
                      (not this.isProjectDRMX)
                      "Autopilot is only available in a BB2 project"
                      ""
                    }}
                    class="{{if
                        (not this.isProjectDRMX)
                        'cursor-not-allowed opacity-50 bg-gray-200'
                        ''
                      }}"
                    for="toggle"
                    {{on "click" (fn this.handleDisabledToggleClick this.isProjectDRMX)}}
                  ></label>
                </div>
              </div>
            </div>
          </label>
        </div>
      </nav>
      <div class="overflow-auto parts bg-gray-50 dark:bg-primary-darkFill">
        <div
          data-test="song-player"
          class="grid items-start gap-1 p-2 grid-cols-song-parts bg-gray-50 dark:bg-primary-darkFill"
          {{sortable
            draggable="[data-part-type=part]"
            handle=".drag-handle"
            group="parts"
            removeItem=true
            onChange=this.sortParts
          }}
        >
          <div class="sticky top-0 z-10 grid col-span-6 grid-cols-subgrid">
            <div class="text-xs text-center bg-gray-200 dark:bg-primary-darkHeader">Name</div>
            <div class="text-xs text-center bg-gray-200 dark:bg-primary-darkHeader">Main Beat</div>
            <div class="text-xs text-center bg-gray-200 dark:bg-primary-darkHeader">Drum Fill(s)</div>
            <div class="text-xs text-center bg-gray-200 dark:bg-primary-darkHeader">Transition</div>
            <div class="text-xs text-center bg-gray-200 dark:bg-primary-darkHeader">Accent Hit</div>
            <div class="text-xs text-center bg-gray-200 dark:bg-primary-darkHeader">Options</div>
          </div>
          <Part
            @type={{PartType.INTRO}}
            @part={{@song.intro}}
            @name="Intro"
            @addPart={{if this.canAddPart (fn this.addPart undefined)}}
            data-test-part="intro"
            @selectTrack={{fn this.selectTrack @song.intro}}
            @addTrack={{fn this.addTrack @song.intro}}
            @importMidi={{fn this.importMidiFile @song.intro}}
            @importMultiMidi={{fn this.importMidiFiles @song.intro}}
          />
          {{~#each @song.parts as |part i|~}}
            <Part
              @type={{PartType.PART}}
              @part={{part}}
              @name={{concat "Part " (add i 1)}}
              @addPart={{if this.canAddPart (fn this.addPart part)}}
              @removePart={{fn this.removePart part}}
              @pastePart={{fn this.pastePart part}}
              @duplicatePart={{if this.canAddPart (fn this.duplicatePart part)}}
              data-test-part={{i}}
              @selectTrack={{fn this.selectTrack part}}
              @addTrack={{fn this.addTrack part}}
              @importMidi={{fn this.importMidiFile part}}
              @importMultiMidi={{fn this.importMidiFiles part}}
            />
          {{~/each~}}
          <Part
            @type={{PartType.OUTRO}}
            @part={{@song.outro}}
            @name="Outro"
            data-test-part="outro"
            @selectTrack={{fn this.selectTrack @song.outro}}
            @addTrack={{fn this.addTrack @song.outro}}
            @importMidi={{fn this.importMidiFile @song.outro}}
            @importMultiMidi={{fn this.importMidiFiles @song.outro}}
          />
        </div>
      </div>
      <div
        class="flex justify-center bg-gray-50 dark:bg-primary-darkFill py-1"
      >
      </div>
      <div class="h-2 cursor-ns-resize handle flex justify-center items-center bg-gray-200 dark:bg-gray-700">
        <svg class="w-4 h-4 text-gray-500 dark:text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-width="2" d="M4 10h16M4 14h16" />
        </svg>
      </div>
      <div
        class="flex flex-col p-2 track
          {{if
            this.isTrackMaximized
            'fixed inset-0 z-50 bg-white dark:bg-primary-darkFill '
          }}"
      >
        {{#if this.selectedTrack}}
          <nav
            aria-label="track-menu"
            class="grid items-center grid-cols-[1fr_minmax(auto,_3fr)_1fr] gap-2"
          >
            <div class="flex gap-2">
              <Button
                @icon="play"
                title="Play"
                {{selected when=this.songPlayer.isPlaying}}
                {{on "click" (fn this.play false)}}
              />
              <Button @icon="stop" title="Stop" {{on "click" this.stop}} />
              <Button
                @icon="arrows-repeat"
                title="Loop track"
                {{selected when=this.loop}}
                {{on "click" this.toggleLoop}}
              />
            </div>
            <EditableField
              @model={{this.selectedTrack.track}}
              @field="name"
              @onChange={{this.renameTrack}}
              class="w-full px-2 py-1 font-semibold text-center"
            >{{this.selectedTrack.track.name}}</EditableField>
            <div class="flex justify-end gap-2">
              {{#if this.isTrackMaximized}}
                <img
                  src="/images/editor/singularsound-purple.png"
                  alt="BeatBuddy Manager Online Logo"
                  class="h-8"
                />
                <Button
                  @icon="arrow-rotate-left"
                  title="Undo"
                  {{disabled when=(not this.undoManager.canUndo)}}
                  {{on "click" this.undoManager.undo}}
                />
                <Button
                  @icon="arrow-rotate-right"
                  title="Redo"
                  {{disabled when=(not this.undoManager.canRedo)}}
                  {{on "click" this.undoManager.redo}}
                />
              {{/if}}
              <Button
                @icon="folder-open"
                title="Import MIDI file"
                {{on "click" this.importMidiFile}}
              />
              <Button
                @icon="floppy-disk"
                title="Save track as MIDI file"
                {{on "click" this.exportMidiFile}}
              />
              {{#if this.songPlayer.selectedTrack}}
                <Button @icon="copy" title="Copy track" {{on "click" this.copyTrack}} />
                <Button
                  @icon="paste"
                  title="Paste track"
                  {{disabled when=(not this.hasCopiedTrack)}}
                  {{on "click" this.pasteTrack}}
                />
                <Button
                  @icon="link-slash"
                  title="Unlink track"
                  {{on "click" this.unlinkTrack}}
                  {{disabled when=(not this.isLinkedTrack)}}
                />
                <Button
                  @icon="trash-can"
                  title="Delete track"
                  {{on "click" this.removeTrack}}
                />
                <Button
                  @icon={{if this.isTrackMaximized "arrows-minimize" "arrows-maximize"}}
                  title="Maximize track editor"
                  {{on "click" this.toggleTrackMaximized}}
                />
              {{/if}}
            </div>
          </nav>
          <TrackEditor
            @selectedTrack={{this.selectedTrack}}
            @drumset={{this.drumset}}
            @trackPosition={{this.trackPosition}}
            @setTrackPosition={{this.setTrackPosition}}
            @isTrackMaximized={{this.isTrackMaximized}}
          />
        {{/if}}
      </div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    SongEditor: typeof SongEditorComponent;
  }
}
